import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { withBranch } from '../../utils/branchRequest';

const API_URL = '/api/services';

export const fetchServices = createAsyncThunk('services/fetchAll', async (params, { rejectWithValue }) => {
  try {
      const response = await axios.get(API_URL, { params });
      return response.data;

  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
});

export const createService = createAsyncThunk('services/create', async (payload, { rejectWithValue }) => {
  try {
      // __branchId (picked before the form opened) pins the create to that branch.
      const { __branchId, ...data } = payload || {};
      const response = __branchId
        ? await axios.post(API_URL, data, withBranch(__branchId))
        : await axios.post(API_URL, payload);
      return response.data;

  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
});

export const updateService = createAsyncThunk('services/update', async ({ id, data }, { rejectWithValue }) => {
  try {
      const response = await axios.put(`${API_URL}/${id}`, data);
      return response.data;

  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
});

export const deleteService = createAsyncThunk('services/delete', async (id, { rejectWithValue }) => {
  try {
      await axios.delete(`${API_URL}/${id}`);
      return id;

  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
});

const serviceSlice = createSlice({
    name: 'services',
    initialState: {
        items: [],
        loading: false,
        error: null,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchServices.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchServices.fulfilled, (state, action) => {
                state.loading = false;
                state.items = action.payload.data;
            })
            .addCase(fetchServices.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.error || action.payload?.message || action.error.message;
            })
            .addCase(createService.fulfilled, (state, action) => {
                state.items.push(action.payload.data);
                // Re-sort items by name
                state.items.sort((a, b) => a.name.localeCompare(b.name));
            })
            .addCase(updateService.fulfilled, (state, action) => {
                const index = state.items.findIndex((item) => item.id === action.payload.data.id);
                if (index !== -1) {
                    state.items[index] = action.payload.data;
                }
            })
            .addCase(deleteService.fulfilled, (state, action) => {
                state.items = state.items.filter((item) => item.id !== action.payload);
            });
    },
});

export default serviceSlice.reducer;
